import { List } from 'immutable'
import React, { ReactElement } from 'react'

import { CostCenterAccounting } from '../../../model/accountingIntegration'
import Company from '../../../model/company'
import CostCenter from '../../../model/costCenter'
import Employee from '../../../model/employee'
import SalaryCycle from '../../../model/salaryCycle'
import { DateFormat } from '../../../model/types'
import { OneTimePayReducer } from '../../../reducers/oneTimePays'
import { FormComponentProps, withValidations } from '../../../utils/form-utils'
import { forceParseInputNumber, formatInputNumber, parseInputNumber } from '../../../utils/number-utils'
import { setByPath } from '../../../utils/object-utils'
import { t } from '../../../utils/translation-utils'
import Button from '../../elements/button'
import Col from '../../elements/grid/col'
import Row from '../../elements/grid/row'
import OneTimePayAccountingText from '../../form-elements/one-time-pay/OneTimePayAccountingText'
import OneTimePayAmount from '../../form-elements/one-time-pay/OneTimePayAmount'
import OneTimePayApproved from '../../form-elements/one-time-pay/OneTimePayApproved'
import OneTimePayCostCenter from '../../form-elements/one-time-pay/OneTimePayCostCenter'
import OneTimePayDispositionDate from '../../form-elements/one-time-pay/OneTimePayDispositionDate'
import OneTimePayTitle from '../../form-elements/one-time-pay/OneTimePayTitle'
import LoadingOverlay from '../../widgets/LoadingOverlay'

type Props = {
  editing: boolean
  canApproveObjects: boolean
  company: Company
  employee: Employee
  oneTimePayID?: string
  oneTimePays: OneTimePayReducer
  salaryCycle: SalaryCycle
  costCenterAccounting: CostCenterAccounting
  costCenters: List<CostCenter>
  noPeriod?: boolean
  prefill?: ReimbursementResult
}

type Fields = {
  dispositionDate?: DateFormat
  amount?: string
  rate?: string
  units?: string
  useRate: boolean
  title?: string
  costCenterID?: string
  accountingText?: string
  approved: boolean
}

export type ReimbursementResult = {
  dispositionDate?: DateFormat
  amount: number
  rate?: number
  units?: number
  useRate: boolean
  title?: string
  costCenterID?: string
  accountingText?: string
  approved: boolean
}

function ReimbursementEditForm(props: Props & FormComponentProps<Fields, ReimbursementResult>): ReactElement | null {
  return (
    <div>
      {props.getFormError()}
      <Row>
        {!props.noPeriod && (
          <Col span={12}>
            <OneTimePayDispositionDate
              {...props}
              oneTimePayID={props.oneTimePayID}
              oneTimePays={props.oneTimePays.oneTimePays.toArray()}
              title={t('reimbursements.edit.form.disposition_date')}
              placeholder={t('reimbursements.edit.form.disposition_date.placeholder')}
            />
          </Col>
        )}
        <Col span={12}>
          <OneTimePayAmount
            {...props}
            description={t('reimbursements.edit.form.amount')}
            disableRate={false}
            disabled={false}
          />
        </Col>
      </Row>
      <OneTimePayApproved {...props} />
      <OneTimePayCostCenter {...props} />
      <Row>
        <Col span={24}>
          <OneTimePayTitle {...props} />
        </Col>
      </Row>
      <OneTimePayAccountingText {...props} />
      {props.editing && (
        <Row>
          <Col span={24}>
            <Button htmlType="submit" size="extra-extra-large" type="primary">
              {t('form.button.save_changes')}
            </Button>
          </Col>
        </Row>
      )}
      {props.oneTimePays.saving && <LoadingOverlay />}
    </div>
  )
}

export default withValidations<Props, Fields, ReimbursementResult>({
  mapPropsToFields: (props) => {
    const fields: Fields = {
      useRate: false,
      approved:
        !props.company.settingsEnabled.some((setting) => setting === 'AskForOTPDraftState') && props.canApproveObjects,
    }
    if (props.oneTimePayID) {
      props.oneTimePays.oneTimePays.forEach((oneTimePay) => {
        if (props.oneTimePayID === oneTimePay.id) {
          fields.dispositionDate = oneTimePay.dispositionDate
          fields.amount = formatInputNumber(oneTimePay.amount)
          fields.rate = oneTimePay.rate ? formatInputNumber(oneTimePay.rate) : undefined
          fields.units = oneTimePay.units ? formatInputNumber(oneTimePay.units) : undefined
          if (!!fields.rate && !!fields.units) {
            fields.useRate = true
          }
          fields.title = oneTimePay.title
          fields.costCenterID = oneTimePay.costCenterID
          fields.accountingText = oneTimePay.accountingText
          fields.approved = oneTimePay.approved
        }
      })
    }
    if (props.prefill) {
      fields.dispositionDate = props.prefill.dispositionDate
      fields.amount = '' + props.prefill.amount
      fields.rate = '' + props.prefill.rate
      fields.units = '' + props.prefill.units
      fields.useRate = props.prefill.useRate
      fields.title = props.prefill.title
      fields.costCenterID = props.prefill.costCenterID
      fields.accountingText = props.prefill.accountingText
      fields.approved = props.prefill.approved
    }

    return fields
  },
  onChange: (key, val, allValues, options) => {
    const values = {}
    switch (key) {
      case 'amount':
      case 'rate':
        setByPath(
          values,
          key,
          formatInputNumber(parseInputNumber(val as string, { trim: options.trigger === 'onBlur' }))
        )
        break
      default:
        setByPath(values, key, val)
        break
    }
    return values
  },
  onSubmit: (values) => ({
    ...values,
    dispositionDate: values.dispositionDate!,
    title: values.title!,
    amount: values.useRate ? 0 : forceParseInputNumber(values.amount),
    rate: values.useRate ? forceParseInputNumber(values.rate) : undefined,
    units: values.useRate ? forceParseInputNumber(values.units) : undefined,
    accountingText: values.accountingText || undefined,
  }),
})(ReimbursementEditForm)
